import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, type TenantMe, type OnboardingStatus } from '../lib/api';
import { setSupabaseAuth } from '../lib/supabase';

interface User {
  id: string;
  email: string;
}

interface AuthState {
  user: User | null;
  tenant: TenantMe | null;
  onboarding: OnboardingStatus | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshOnboarding: () => Promise<void>;
  refreshTenant: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<TenantMe | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOnboarding = useCallback(async () => {
    try {
      const status = await api.getOnboardingStatus();
      setOnboarding(status);
    } catch {
      // If onboarding endpoint fails, assume not completed
      setOnboarding({ current_step: 0, completed_steps: [], total_steps: 2, onboarding_completed: false });
    }
  }, []);

  const fetchTenant = useCallback(async () => {
    const t = await api.getMe();
    setTenant(t);
    setUser({ id: t.id, email: t.email });
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      // DSH-01: repassa o refresh token persistido — sem ele a sessão
      // supabase não nasce no boot (e o realtime ficava só no setAuth).
      setSupabaseAuth(token, localStorage.getItem('raios_refresh') ?? undefined);
      Promise.all([
        api.getMe().then((t) => {
          setTenant(t);
          setUser({ id: t.id, email: t.email });
        }),
        api.getOnboardingStatus().then(setOnboarding).catch(() => setOnboarding(null)),
      ])
        .catch(() => {
          api.logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const login = async (email: string, password: string) => {
    const data = await api.login(email, password);
    setSupabaseAuth(data.access_token, data.refresh_token);
    const t = await api.getMe();
    let ob: OnboardingStatus;
    try {
      ob = await api.getOnboardingStatus();
    } catch {
      ob = { current_step: 0, completed_steps: [], total_steps: 2, onboarding_completed: false };
    }
    // Set all state at once to avoid intermediate renders
    setTenant(t);
    setOnboarding(ob);
    setUser(data.user);
  };

  const logout = () => {
    api.logout();
    // Clear onboarding session cache to prevent stale data on re-login
    sessionStorage.removeItem('raios_onboarding_collected');
    sessionStorage.removeItem('raios_onboarding_messages');
    setUser(null);
    setTenant(null);
    setOnboarding(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        onboarding,
        loading,
        login,
        logout,
        refreshOnboarding: fetchOnboarding,
        refreshTenant: fetchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
